import axios from 'axios';
import { concat, cloneDeep, each, find, remove, map, findIndex } from 'lodash';
import { fetch as fetchPolyfill } from 'whatwg-fetch';
import uuid from 'uuid/v4';
import { getGeoMappingRequestsData, setParentChildrenCountMap,
  extractAllNodes, getGeographiesCount, compareExpArr } from '../util/geomapUtils';
import * as urls from './urlTypes';
import * as types from './actionTypes';
import { addIndexToGeoMapData } from '../util/heatMapUtil';

export function getGeoMappingStudies(token, userId, accountId) {
  const url = `${urls.ROOT_URL}/geomapping/studies/${userId}/${accountId}`;
  return dispatch => axios({
    url,
    method: 'get',
    headers: { Authorization: `Bearer ${token}`, Accept: 'application/json;charset=UTF-8' },
    withCredentials: true,
    responseType: 'json',
  })
    .then(response => {
      if (response.data.data && response.data.data.length === 0) {
        dispatch({
          type: types.NO_GEOMAPPING_STUDIES,
          payload: false,
        });
        return false;
      }
      dispatch({
        type: types.GET_GEOMAPPING_STUDY,
        payload: response.data,
      });
      return true;
    })
    .catch(response => {
      console.warn(response);
      return false;
    });
}

export const setGeoMapBaseTarget = (targetBaseInfo, targetGrid) => {
  return dispatch =>
    new Promise(resolve => {
      dispatch({
        type: types.SET_GEOMAP_BASE_TARGET,
        targetGrid,
        payload: targetBaseInfo,
        uuid: uuid(),
      });
      resolve(true);
    });
};

// Thunks - actions that return a function that accepts a dispatch
export const clearSampleWeighted = (targetGrid, updatedGeographicsTableApiQueuesList) => {
  return dispatch =>
    new Promise(resolve => {
      dispatch({
        type: types.CLEAR_GEOMAP_REPORTS_SAMPLE_WEIGHTED,
        targetGrid,
        updatedGeographicsTableApiQueuesList,
        uuid: uuid(),
      });
      resolve(true);
    });
};

export const fetchUpdatedSampleWeightedThunk = payload => ({
  type: types.UPDATE_TARGET_SAMPLE_AND_WEIGHT,
  payload,
});

export const fetchKPIThunk = payload => ({
  type: types.FETCH_GEOMAPPING_SMART_SEARCH_KPI,
  payload,
});

export const getUpdatedTargetSampleAndWeight = (
  targetInfo,
  uid,
  accountId,
  productNo,
  weightType,
  divisor,
  expressionList,
) => {
  return dispatch => {
    const url = `${urls.ROOT_URL}/geomapping/samplewgtd`;
    const requestData = {
      uid,
      accountId,
      productNo,
      weightType,
      divisor,
      expressionList,
    };
    axios({
      url,
      method: 'post',
      headers: {
        Accept: 'application/json;charset=UTF-8',
      },
      withCredentials: true,
      responseType: 'json',
      validateStatus: status => status >= 200 && status < 202,
      data: requestData,
    })
      .then(response => {
        const payload = {
          ...response.data.data,
          expressionList: requestData.expressionList,
          productNo: requestData.productNo,
          response: response.data.data,
          targetInfo,
        };
        dispatch(fetchUpdatedSampleWeightedThunk(payload));
      });
  };
};

export const resetExpApiQueue = () => ({
  type: types.RESET_EXP_API_QUEUE,
});

export const fetchKPI = ({
 token,
 uid,
 accountId,
 productNo,
 weightType,
 divisor,
 expressionList,
 kpiExpression,
}, expArr) => {
  return (dispatch, getState) => {
    weightType = cloneDeep(getState().study.geoWeightTypes.wt);
    divisor = cloneDeep(getState().study.geoWeightTypes.dv);
    const url = `${urls.ROOT_URL}/geomapping/samplewgtd`;
    const requestData = {
      uid,
      accountId,
      productNo,
      weightType,
      divisor,
      expressionList,
    };
    axios({
      url,
      method: 'post',
      headers: {
        Accept: 'application/json;charset=UTF-8',
      },
      withCredentials: true,
      responseType: 'json',
      validateStatus: status => status >= 200 && status < 202,
      data: requestData,
      expListArr: expArr,
    })
      .then(response => {
        const expApiQueue = cloneDeep(getState().geomap.expApiQueue);
        const expList = response.config.expListArr;
        const payload = {
          ...response.data.data,
          expressionList: requestData.expressionList,
          productNo: requestData.productNo,
          response: response.data.data,
          kpiExpression,
        };
        if (compareExpArr(expList, expApiQueue)) {
          dispatch(resetExpApiQueue());
          dispatch(fetchKPIThunk(payload));
        }
      })
      .catch(response => console.warn(response));
  };
};

export const removeGeomapKpiInfo = () => ({
  type: types.REMOVE_GEOMAP_KPI,
});

export const updateSelectedGeographicsTab = slectedTab => ({
  type: types.UPDATE_SELECTED_GEOGRAPHICS_TAB,
  payload: slectedTab,
});

export const fetchGeoghrapicsDataThunk = (payload, selectedTab) => ({
  type: types.FETCH_GEOGRAPHICS_DATA,
  payload,
  selectedTab,
});

export const updateCountyAndZipcodeCategory = (selectedTab, selectedCategory) => ({
  type: types.UPDATE_COUNTY_AND_ZIPCODE_CATEGORY,
  payload: {
    selectedTab,
    selectedCategory,
  },
});

export const setParentChildCountMapThunk = (payload, selectedTab) => ({
  type: types.SET_PARENT_CHILD_COUNT_MAP,
  payload,
  selectedTab,
});

export const setExtratedNodesThunk = (payload, selectedTab) => ({
  type: types.SET_EXTRACTED_NODES,
  payload,
  selectedTab,
});

export const setGeographicsCountThunk = (count, selectedTab) => ({
  type: types.SET_GEOGRAPHIES_COUNT,
  payload: count,
  selectedTab,
});

export const getGeoghrapicsData = (
  uid,
  accountId,
  productNo,
  topNodeVal,
  selectedTabValue,
) => {
  return dispatch =>
    new Promise(resolve => {
      const url = `${urls.ROOT_URL}/geomapping/geodata/${uid}/${accountId}/${productNo}/${topNodeVal}`;
      selectedTabValue = selectedTabValue.replace(/\s/g, '');
      fetchPolyfill(url, {
        method: 'get',
        headers: {
          Accept: 'application/json;charset=UTF-8',
          'Content-Type': 'application/json',
        },
        withCredentials: true,
        credentials: 'include',
        responseType: 'json',
        validateStatus: status => status >= 200 && status < 202,
      })
        .then(response => response.json())
          .then(data => {
            const payload = data.data;
            let childCountsMap = {};
            let extractedPayload = [];
            let geographicsCount = 0;
            dispatch(fetchGeoghrapicsDataThunk(payload, selectedTabValue));
            if (selectedTabValue === 'countyByState' || selectedTabValue === 'countyByMarket'
            || selectedTabValue === 'zipcodeByState' || selectedTabValue === 'zipcodeByMarket') {
              childCountsMap = setParentChildrenCountMap(payload);
            }
            dispatch(setParentChildCountMapThunk(childCountsMap, selectedTabValue));
            extractedPayload = extractAllNodes(payload);
            dispatch(setExtratedNodesThunk(extractedPayload, selectedTabValue));
            geographicsCount = getGeographiesCount(extractedPayload);
            dispatch(setGeographicsCountThunk(geographicsCount, selectedTabValue));
            resolve(true);
          });
    });
};

// geo map tabs table data...
export const setGeoghrapicsTableDataThunk = (payload, selectedTab) => {
  return dispatch =>
  new Promise(resolve => {
    dispatch({
      type: types.SET_GEOGRAPHICS_TABLE_DATA,
      payload,
      selectedTab,
    });
    resolve(true);
  });
};

export const fetchGeoghrapicsTableApiQueueThunk = (payload, selectedTab) => {
  return dispatch =>
  new Promise(resolve => {
    dispatch({
      type: types.FETCH_GEOGRAPHICS_TABLE_API_QUEUES,
      payload,
      selectedTab,
    });
    resolve(true);
  });
};

export const resetGeoghrapicsApiQueues = (queues, selectedTab) => {
  return dispatch => {
    dispatch({
      type: types.RESET_GEOGRAPHICS_TAB_API_QUEUES,
      queues,
      selectedTab,
    });
  };
};

export const resetGeoghrapicsApiKeysQueues = (queues, selectedTab) => {
  return dispatch => {
    dispatch({
      type: types.RESET_GEOGRAPHICS_API_KEYS_QUEUES,
      queues,
      selectedTab,
    });
  };
};

export const resetGeoMapTablesWithZerosAndApis = () => {
  return (dispatch, getState) =>
  new Promise(resolve => {
    const tabNames = ['county', 'censusRegion', 'state', 'market', 'zipcode'];
    const geoTableData = cloneDeep(getState().geomap.geographicsTableData);
    const newGeoTableData = {};
    const newGeoApiRequests = {};
    each(tabNames, tabName => {
      if (geoTableData[tabName].length > 0) {
        const selectedTableData = cloneDeep(geoTableData[tabName]);
        const modifiedSelectedTableData = selectedTableData.map(item => {
          const temp = cloneDeep(item);
          temp.ts = 0;
          temp.tw = 0;
          temp.bw = 0;
          temp.h = 0;
          return temp;
        });
        const geoRequests = getGeoMappingRequestsData(modifiedSelectedTableData);
        newGeoTableData[tabName] = modifiedSelectedTableData;
        newGeoApiRequests[tabName] = geoRequests;
      } else {
        newGeoTableData[tabName] = [];
        newGeoApiRequests[tabName] = [];
      }
    });
    dispatch({
      type: types.RESET_GEOGRAPHICS_TABLE_DATA_APIS_API_QUEUES,
      geographicsTableApiQueues: newGeoApiRequests,
      geographicsTableData: newGeoTableData,
    });
    resolve(true);
  });
};

export const setGeoghrapicsApiQueues = (queues, selectedTab) => {
  return (dispatch, getState) =>
    new Promise(resolve => {
      const selectedTabApiQueue =
          cloneDeep(getState().geomap.geographicsTableApiQueues[selectedTab]);
      each(queues, queue => {
        selectedTabApiQueue.push(queue);
      });
      dispatch(fetchGeoghrapicsTableApiQueueThunk(selectedTabApiQueue, selectedTab));
      resolve(true);
    });
};

export const getGeoghrapicsTableData = (
  uid,
  accountId,
  productNo,
  weightType,
  selectedTabVal,
  rowExpressionList,
  rowHeaderList,
  rowPathIds,
  rowSpIds,
  rowIds,
  apiuuid,
) => {
  return (dispatch, getState) => {
    weightType = getState().study.geoWeightTypes.wt;
    const targetMap = getState().geomap.TargetDataGridData;
    if (targetMap.length > 0) {
      const targetExpression = targetMap[0].keyword;
      const targetHeader = targetMap[0].originalTitle;
      const baseExpression = getState().geomap.BaseDataGridData.length > 0 ?
            getState().geomap.BaseDataGridData[0].keyword : undefined;
      const baseHeader = getState().geomap.BaseDataGridData.length > 0 ?
            getState().geomap.BaseDataGridData[0].originalTitle : undefined;
      const targetuuid = getState().geomap.uuid;
      const url = `${urls.ROOT_URL}/geomapping/report/${uid}/${accountId}`;
      let requestData = {};
      if (baseExpression === undefined) {
        requestData = {
          productNo,
          weightType,
          targetExpression,
          targetHeader,
          rowExpressionList,
          rowHeaderList,
          rowPathIds,
          rowSpIds,
          rowIds,
          attributes: {
            apiuuid,
            targetuuid,
            selectedTabVal,
          },
        };
      } else {
        requestData = {
          productNo,
          weightType,
          targetExpression,
          targetHeader,
          rowExpressionList,
          rowHeaderList,
          baseExpression,
          baseHeader,
          rowPathIds,
          rowSpIds,
          rowIds,
          attributes: {
            apiuuid,
            targetuuid,
            selectedTabVal,
          },
        };
      }
      fetchPolyfill(url, {
        method: 'post',
        headers: {
          Accept: 'application/json;charset=UTF-8',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        validateStatus: status => status >= 200 && status < 202,
        body: JSON.stringify(requestData),
      })
      .then(response => response.json())
      .then(data => {
        const payload = data.data.results;
        const originalTargetuuid = getState().geomap.uuid;
        const apiuuidFromApi = data.data.attributes.apiuuid;
        const targetuuid = data.data.attributes.targetuuid;
        const selectedTabValue = data.data.attributes.selectedTabVal;
        if (originalTargetuuid === targetuuid) {
          const apiQueues =
          cloneDeep(getState().geomap.geographicsTableApiQueues[selectedTabValue]);
          if (find(apiQueues, { apiuuid: apiuuidFromApi }) !== undefined) {
            remove(apiQueues, { apiuuid: apiuuidFromApi });
            dispatch(fetchGeoghrapicsTableApiQueueThunk(apiQueues, selectedTabValue));
            const selectedTabAvailabelData =
             cloneDeep(getState().geomap.geographicsTableData[selectedTabValue]);
            // const mergedData = unionBy(payload, selectedTabAvailabelData, 'pid');
            // dispatch(setGeoghrapicsTableDataThunk(mergedData, selectedTabValue));

            map(payload, obj => {
              const index = findIndex(selectedTabAvailabelData, { pid: obj.pid });
              selectedTabAvailabelData[index] = obj;
            });
            
            dispatch(setGeoghrapicsTableDataThunk(addIndexToGeoMapData(selectedTabAvailabelData), selectedTabValue));
          }
        }
        dispatch(updateGeoMapLoading(false))
        /* else {
          resetGeoMapTablesWithZerosAndApis().then(() => {
            const geoMapTarget = getState().geomap.TargetDataGridData;
            if (geoMapTarget.length > 0) {
              const requestapiQueues = cloneDeep(getState().geomap.geographicsTableApiQueues[selectedTabValue]);
              for (let k = 0; k < requestapiQueues.length; k += 1) {
                getGeoghrapicsTableDataV2(
                  uid,
                  accountId,
                  productNo,
                  weightType,
                  data.data.attributes.selectedTabVal,
                  requestapiQueues[k].rowExpressionList,
                  requestapiQueues[k].rowHeaderList,
                  requestapiQueues[k].rowPathIds,
                  requestapiQueues[k].apiuuid);
              }
            }
          });
        } */
      });
    }
  };
};

export const deleteGeographiesTableData = payload => ({
  type: types.DELETE_GEOGRAPHIES_TABLE_DATA,
  payload,
});

export const exportGeoMap = (
  uid, accountId, exportRequest
) => {
  const url = `${urls.ROOT_URL}/reports/export/geomapping/${uid}/${accountId}`;
  return fetchPolyfill(url, {
    method: 'post',
    headers: {
      Accept: 'application/json;charset=UTF-8',
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    withCredentials: true,
    responseType: 'json',
    body: JSON.stringify(exportRequest),
    validateStatus: status => status >= 200 && status < 202,
  })
    .catch(response => console.warn(response));
};


export const updateParentDraggedNodes = (payload, selectedTab) => ({
  type: types.UPDATE_PARENT_DRAGGED_NODES,
  payload,
  selectedTab,
});

export const setGeoghrapicsTableDataWithZeros = (data, selectedTab) => {
  return (dispatch, getState) =>
    new Promise(resolve => {
      const selectedTabAvailabelData =
        cloneDeep(getState().geomap.geographicsTableData[selectedTab]);
      let payload = data;
      if (selectedTabAvailabelData && selectedTabAvailabelData.length > 0) {
        payload = concat(selectedTabAvailabelData, data);
      }
      dispatch(setGeoghrapicsTableDataThunk(payload, selectedTab));
      resolve(true);
    });
};

export const updateSampleWeighted = (gridData, targetGrid) => ({
  type: types.UPDATE_GEOMAP_SAMPLE_WEIGHTED,
  gridData,
  targetGrid,
});

export const updateGeoMapGrid = payload => ({
  type: types.UPDATE_GEOMAP_SORT_DATA,
  payload,
});

export const buildSamplewtApiOnStudyChange = (
  uid,
  accountId,
  productNo,
  weightType,
  divisor,
  expressionList,
  tabVal,
) => {
  return () =>
    new Promise(resolve => {
      const url = `${urls.ROOT_URL}/geomapping/samplewgtd`;
      const requestData = {
        uid,
        accountId,
        productNo,
        weightType,
        divisor,
        expressionList,
      };
      axios({
        url,
        method: 'post',
        headers: {
          Accept: 'application/json;charset=UTF-8',
        },
        withCredentials: true,
        responseType: 'json',
        validateStatus: status => status >= 200 && status < 202,
        data: requestData,
        selectedTab: tabVal,
      })
      .then(response => {
        if (response.data.data && response.data.data.length > 0) {
          const responseData = {
            selectedTab: response.config.selectedTab,
            data: response.data.data,
          };
          resolve(responseData);
        }
      });
    });
};

export const buildGeoTableApiOnStudyChange = (
  uid,
  accountId,
  productNo,
  topNodeVal,
  requestData,
  tabVal,
) => {
  return () =>
  new Promise(resolve => {
    const url = `${urls.ROOT_URL}/geomapping/geodata/validate/${uid}/${accountId}/${productNo}/${topNodeVal}`;
    axios({
      url,
      method: 'post',
      headers: {
        Accept: 'application/json;charset=UTF-8',
        'Content-Type': 'application/json',
      },
      withCredentials: true,
      responseType: 'json',
      validateStatus: status => status >= 200 && status < 202,
      data: requestData,
      selectedTab: tabVal,
    }).then(response => {
      if (response.data.data) {
        const responseData = {
          selectedTab: response.config.selectedTab,
          data: response.data.data,
        };
        resolve(responseData);
      }
    });
  });
};

export const updateBaseAndTargetOnStudyChange = (targetInfo, baseInfo) => {
  return dispatch =>
  new Promise(resolve => {
    dispatch({
      type: types.UPDATE_GEOMAP_TARGET_AND_BASE_ON_STUDY_CHANGE,
      targetInfo,
      baseInfo,
    });
    resolve(true);
  });
};
export const setOriginalTitle = (targetGrid, payload) => ({
  type: types.SET_ORIGINAL_TITLE,
  payload,
  targetGrid,
});

export const resetGeographicsTableDataWithZeros = geographicsTableData => ({
  type: types.RESET_GEOGRAPHICS_TABLE_DATA_WITH_ZEROES,
  geographicsTableData,
});

export const setExpApiQueue = payload => {
  return dispatch =>
  new Promise(resolve => {
    dispatch({
      type: types.SET_EXP_API_QUEUE,
      payload,
    });
    resolve(true);
  });
};

export const setGeoghrapicsTableApiCallStatus = (payload, selectedTab) => {
  return dispatch =>
  new Promise(resolve => {
    dispatch({
      type: types.SET_GEOGRAPHICS_TABLE_API_CALL_STATUS,
      payload,
      selectedTab,
    });
    resolve(true);
  });
};


export const setExportSpinnerFlag = payload => {
  return dispatch =>
  new Promise(resolve => {
    dispatch({
      type: types.SET_EXPORT_SPINNER_FLAG,
      payload,
    });
    resolve(true);
  });
};

export const setExportSpinnerWarningInfoMsg = payload => {
  return dispatch =>
  new Promise(resolve => {
    dispatch({
      type: types.EXPORT_PROGRESS_WARNING_MSG_FLAG,
      payload,
    });
    resolve(true);
  });
};

export const updateMapSettings = (key, value) => dispatch => {
  dispatch({
    type: types.UPDATE_GEO_MAP_SETTINGS,
    key,
    value,
  })
}

export const updateCompleteMapSettings = (data) => dispatch => {
  dispatch({
    type: types.UPDATE_COMPLETE_GEO_MAP_SETTINGS,
    data
  })
}

export const updateGeoMapLoading = (value) => dispatch => {
  dispatch({
    type: types.LOADING_GEO_MAP_TABLE_DATA,
    value
  })
}

// export const setExportSpinnerFlag = payload => {
//   return dispatch => {
//     dispatch({
//       type: types.SET_EXPORT_SPINNER_FLAG,
//       payload,
//     });
//   };
// };

// export const setExportSpinnerWarningInfoMsg = payload => {
//   return dispatch => {
//     dispatch({
//       type: types.EXPORT_PROGRESS_WARNING_MSG_FLAG,
//       payload,
//     });
//   };
// };
